import React, { useEffect, useState, useCallback } from 'react';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import {
  Box,
  Grid,
  Typography,
  Paper,
  Button,
  LinearProgress,
  Fab,
  useTheme,
  alpha,
  Tooltip,
} from '@mui/material';
import CountUp from 'react-countup';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import config from '../../config';
import Calender from './Calender';
import { usePermission } from '../rbac/PermissionProvider';
import { P } from '../rbac/permissionCodes';
import SubscriptionBanner from './SubscriptionBanner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
  Cell,
} from 'recharts';
import HotelIcon from '@mui/icons-material/Hotel';
import BedIcon from '@mui/icons-material/Bed';
import BedroomParentIcon from '@mui/icons-material/BedroomParent';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import AddIcon from '@mui/icons-material/Add';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LockIcon from '@mui/icons-material/Lock';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';

const companyData = JSON.parse(
  sessionStorage.getItem('companyDetails') || '{}',
);
const Companycurrency = companyData?.currency ?? '';
const Users = JSON.parse(sessionStorage.getItem('user'));

/* ── Design tokens ── */
const T = {
  blue: '#1A56DB',
  blueL: '#3b82f6',
  green: '#10b981',
  amber: '#f59e0b',
  rose: '#f43f5e',
  purple: '#8b5cf6',
  cyan: '#06b6d4',
  orange: '#f97316',
  pink: '#ec4899',
  border: 'rgba(148,163,184,0.18)',
  bg: '#F1F5F9',
  card: '#ffffff',
  textPri: '#0F172A',
  textSec: '#64748B',
  textMut: '#94A3B8',
};

const shadow = {
  card: '0 1px 3px rgba(15,23,42,0.04), 0 4px 16px rgba(15,23,42,0.04)',
  hover: (color) =>
    `0 4px 12px rgba(15,23,42,0.06), 0 12px 32px ${alpha(color, 0.18)}`,
};

/* ── SVG Occupancy Ring ── */
function OccupancyRing({ pct, size = 88 }) {
  const r = size / 2 - 9;
  const circ = 2 * Math.PI * r;
  const clamped = Math.min(Math.max(pct, 0), 100);
  const dash = (clamped / 100) * circ;
  const cx = size / 2,
    cy = size / 2;
  return (
    <svg
      width={size}
      height={size}
      viewBox={`0 0 ${size} ${size}`}
      style={{ flexShrink: 0 }}>
      <defs>
        <linearGradient id='ring-grad' x1='0%' y1='0%' x2='100%' y2='100%'>
          <stop offset='0%' stopColor='#60a5fa' />
          <stop offset='100%' stopColor='#ffffff' />
        </linearGradient>
      </defs>
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill='none'
        stroke='rgba(255,255,255,0.18)'
        strokeWidth={7}
      />
      <circle
        cx={cx}
        cy={cy}
        r={r}
        fill='none'
        stroke='url(#ring-grad)'
        strokeWidth={7}
        strokeDasharray={`${dash} ${circ}`}
        strokeLinecap='round'
        transform={`rotate(-90 ${cx} ${cy})`}
        style={{ transition: 'stroke-dasharray 1.2s ease' }}
      />
    </svg>
  );
}

/* ── Custom Tooltip ── */
function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <Box
      sx={{
        background: '#0F172A',
        borderRadius: '10px',
        p: '8px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)',
      }}>
      <Typography
        sx={{
          fontSize: '0.62rem',
          color: 'rgba(255,255,255,0.55)',
          mb: '2px',
        }}>
        {label}
      </Typography>
      <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#fff' }}>
        {Companycurrency} {Number(payload[0]?.value || 0).toLocaleString()}
      </Typography>
    </Box>
  );
}

/* ── Section Label ── */
function SectionLabel({ label, color = T.blue, action }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '12px' }}>
      <Box
        sx={{
          width: 3,
          height: 16,
          borderRadius: 2,
          background: `linear-gradient(180deg,${color},${alpha(color, 0.3)})`,
        }}
      />
      <Typography
        sx={{
          fontSize: '0.62rem',
          fontWeight: 700,
          color: T.textSec,
          letterSpacing: '0.8px',
          textTransform: 'uppercase',
        }}>
        {label}
      </Typography>
      {action && (
        <Box sx={{ flex: 1, display: 'flex', justifyContent: 'flex-end' }}>
          {action}
        </Box>
      )}
    </Box>
  );
}

/* ── Revenue KPI Card ── */
function KpiCard({ title, value, color, icon, onClick, sub, grad }) {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '16px',
        p: '16px 18px',
        height: '100%',
        background: T.card,
        border: `1px solid ${T.border}`,
        boxShadow: shadow.card,
        transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': onClick
          ? { transform: 'translateY(-4px)', boxShadow: shadow.hover(color) }
          : {},
        display: 'flex',
        alignItems: 'flex-start',
        gap: '14px',
        overflow: 'hidden',
        minWidth: 0,
        position: 'relative',
      }}>
      {/* subtle tinted bg */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `linear-gradient(135deg,${alpha(color, 0.04)} 0%,transparent 60%)`,
          borderRadius: '16px',
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: '12px',
          flexShrink: 0,
          background:
            grad || `linear-gradient(135deg,${color},${alpha(color, 0.7)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 4px 12px ${alpha(color, 0.35)}`,
          '& svg': { fontSize: 22, color: '#fff' },
        }}>
        {icon}
      </Box>
      <Box sx={{ minWidth: 0, flex: 1, zIndex: 1 }}>
        <Typography
          sx={{
            fontSize: '0.58rem',
            fontWeight: 600,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
            color: T.textMut,
            lineHeight: 1,
            mb: '6px',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}>
          {title}
        </Typography>
        <Typography
          sx={{
            fontSize: '1.15rem',
            fontWeight: 800,
            color: T.textPri,
            lineHeight: 1,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            letterSpacing: '-0.3px',
          }}>
          <span style={{ fontSize: '0.6em', color: T.textMut, marginRight: 2 }}>
            {Companycurrency}
          </span>
          <CountUp end={value} separator=',' duration={1.3} />
        </Typography>
        {sub && (
          <Typography sx={{ fontSize: '0.57rem', color: T.textMut, mt: '3px' }}>
            {sub}
          </Typography>
        )}
      </Box>
    </Paper>
  );
}

/* ── Performance Metric Tile ── */
function MetricTile({ title, value, icon, color, onClick, isAmount }) {
  return (
    <Paper
      onClick={onClick}
      elevation={0}
      sx={{
        cursor: onClick ? 'pointer' : 'default',
        borderRadius: '14px',
        p: '14px 10px',
        background: `linear-gradient(145deg,${alpha(color, 0.07)},${alpha(color, 0.02)})`,
        border: `1px solid ${alpha(color, 0.18)}`,
        boxShadow: shadow.card,
        transition: 'transform 0.2s, box-shadow 0.2s',
        textAlign: 'center',
        // Equal size for every tile — long 2-line titles (e.g. "Monthly Room
        // Restaurant") were making some tiles taller than others.
        height: '100%',
        minHeight: '120px',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        '&:hover': onClick
          ? { transform: 'translateY(-3px)', boxShadow: shadow.hover(color) }
          : {},
      }}>
      <Box
        sx={{
          width: 36,
          height: 36,
          borderRadius: '10px',
          mx: 'auto',
          mb: '10px',
          background: `linear-gradient(135deg,${color},${alpha(color, 0.65)})`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxShadow: `0 3px 10px ${alpha(color, 0.35)}`,
          '& svg': { fontSize: 18, color: '#fff' },
        }}>
        {icon}
      </Box>
      <Typography
        sx={{
          fontSize: '1.5rem',
          fontWeight: 800,
          color,
          lineHeight: 1,
          mb: '5px',
          letterSpacing: '-0.5px',
        }}>
        {isAmount && (
          <span
            style={{
              fontSize: '0.5em',
              color: alpha(color, 0.65),
              marginRight: 2,
            }}>
            {Companycurrency}
          </span>
        )}
        <CountUp end={value} separator=',' duration={1.3} />
      </Typography>
      <Typography
        sx={{
          fontSize: '0.57rem',
          fontWeight: 600,
          letterSpacing: '0.4px',
          textTransform: 'uppercase',
          color: alpha(color, 0.65),
        }}>
        {title}
      </Typography>
    </Paper>
  );
}

/* ── Stat Mini Card (for bottom stats) ── */
function StatMini({ label, value, color, icon, pct, pctLabel }) {
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: '14px',
        p: '14px 16px',
        background: T.card,
        border: `1px solid ${T.border}`,
        boxShadow: shadow.card,
        height: '100%',
      }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: '8px',
        }}>
        <Typography
          sx={{
            fontSize: '0.57rem',
            fontWeight: 700,
            color: T.textSec,
            letterSpacing: '0.5px',
            textTransform: 'uppercase',
          }}>
          {label}
        </Typography>
        <Box sx={{ color, '& svg': { fontSize: 16 } }}>{icon}</Box>
      </Box>
      <Typography
        sx={{
          fontSize: '1.4rem',
          fontWeight: 800,
          color,
          lineHeight: 1,
          letterSpacing: '-0.4px',
          mb: '8px',
        }}>
        <CountUp end={value} separator=',' duration={1.5} />
      </Typography>
      <Box
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: alpha(color, 0.12),
          overflow: 'hidden',
        }}>
        <Box
          sx={{
            height: '100%',
            width: `${Math.min(pct, 100)}%`,
            bgcolor: color,
            borderRadius: 2,
            transition: 'width 1.2s ease',
          }}
        />
      </Box>
      {pctLabel && (
        <Typography sx={{ mt: '4px', fontSize: '0.57rem', color: T.textMut }}>
          {pctLabel}
        </Typography>
      )}
    </Paper>
  );
}

export default function PremiumDashboard({
  userName = Users?.name || 'Admin',
}) {
  const navigate = useNavigate();
  const theme = useTheme();

  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [now, setNow] = useState(new Date());
  // #2: the booking calendar is heavy (fetches the whole room grid) — don't
  // auto-load it. Render it only when the user clicks "Load Calendar".
  const [showCalendar, setShowCalendar] = useState(false);

  const [data, setData] = useState({
    hotelCount: 0,
    roomCount: 0,
    addRevenue: 0,
    netCashBalance: 0,
    totalCheckIn: 0,
    totalCheckOut: 0,
    todayAvailableRoomQty: 0,
    todayBookedRoomQty: 0,
    totalSalePayment: 0,
    todaySalePayment: 0,
    monthlysale: 0,
    holdroomqty: 0,
    Todayrestusale: 0,
    Monthlyrestusale: 0,
    totalAgentBookings: 0,
    todayAgentBookings: 0,
    monthlyAgentBookings: 0,
  });

  const [extraData, setExtraData] = useState({
    occupancyRate: 0,
    avgAddOnPerBooking: 0,
  });
  // Most-booked rooms analytics (with Today / Week / Month filter).
  const [topRooms, setTopRooms] = useState([]);
  const [roomPeriod, setRoomPeriod] = useState('today');
  // Recent activity log (who did what) for the dashboard.
  const [activity, setActivity] = useState([]);

  /* ── PERMISSION: read directly from sessionStorage ── */
  const { hasPermission: providerHasPermission } = usePermission();

  const hasPermission = useCallback((permission) => {
    try {
      const stored = JSON.parse(
        sessionStorage.getItem('permissionCodes') || '[]',
      );
      if (Array.isArray(stored) && stored.length > 0) {
        return stored.some((x) => x.code === permission);
      }
    } catch (_) {}
    // fallback to provider if session is empty or parse fails
    return providerHasPermission(permission);
  }, [providerHasPermission]);

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const loadCompany = async () => {
      try {
        const clientValue = localStorage.getItem('client');

        console.log('🔥 Dashboard Client:', clientValue);

        if (!clientValue) {
          setError('Company not found');
          setLoading(false);
          return;
        }

        const res = await fetch(
          `${config.API_BASE_URL}/account/init?client=${clientValue}`,
        );

        if (!res.ok) {
          throw new Error(`Server error: ${res.status}`);
        }

        const d = await res.json();

        console.log('✅ Dashboard Company Loaded:', d);

        sessionStorage.setItem('companyId', d.id);
        sessionStorage.setItem('companyDetails', JSON.stringify(d));

        setCompany(d);

        // Reload only one time
        const hasReloaded = sessionStorage.getItem('dashboardReloaded');

        if (!hasReloaded) {
          sessionStorage.setItem('dashboardReloaded', 'true');
          window.location.reload();
        }
      } catch (err) {
        console.error(err);
        setError('Company initialization failed');
        setLoading(false);
      }
    };
    loadCompany();
  }, []);

  // #1: Dashboard date + hotel filters (empty = today / all hotels).
  const [dashFrom, setDashFrom] = useState('');
  const [dashTo, setDashTo] = useState('');
  const [dashHotel, setDashHotel] = useState('');
  const [dashHotels, setDashHotels] = useState([]);
  const [dashQs, setDashQs] = useState('');
  const applyDashFilter = () => {
    const qp = new URLSearchParams();
    if (dashFrom) qp.set('from', dashFrom);
    if (dashTo) qp.set('to', dashTo);
    if (dashHotel) qp.set('hotelId', dashHotel);
    const s = qp.toString();
    setDashQs(s ? ('?' + s) : '');
  };
  useEffect(() => {
    if (!company?.id) return;
    const tk = sessionStorage.getItem('token');
    fetch(config.API_BASE_URL + '/Home/GetHotelsdropdown/' + company.id, {
      headers: { Authorization: 'Bearer ' + tk },
    })
      .then((r) => r.json())
      .then((d) => setDashHotels(Array.isArray(d) ? d : []))
      .catch(() => setDashHotels([]));
  }, [company?.id]);

  useEffect(() => {
    if (!company?.id || !sessionStorage.getItem('token')) {
      setLoading(false);
      return;
    }
    const token = sessionStorage.getItem('token');
    let mounted = true;
    setLoading(true);
    axios
      .get(`${config.API_BASE_URL}/Home/GetDeshboarddata/${company.id}${dashQs}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        if (!mounted) return;
        const r = res.data || {};
        const todayBooked = r.todayBookedRoomQty ?? 0;
        const todayAvailable = r.todayAvailableRoomQty ?? 0;
        const roomCount = r.roomCount ?? 0;
        const addRevenue = r.addRevenue ?? 0;
        const holdQty = r.holdroomqty ?? r.HoldRoomQty ?? 0;
        setData({
          hotelCount: r.hotelCount ?? 0,
          roomCount,
          addRevenue,
          netCashBalance: r.netCashBalance ?? 0,
          totalCheckIn: r.totalCheckIn ?? 0,
          totalCheckOut: r.totalCheckOut ?? 0,
          todayAvailableRoomQty: todayAvailable,
          todayBookedRoomQty: todayBooked,
          totalSalePayment: r.totalSalePayment ?? 0,
          todaySalePayment: r.todaySalePayment ?? 0,
          monthlysale: r.monthlysale ?? 0,
          holdroomqty: holdQty,
          Monthlyrestusale: r.monthlyrestPayment ?? 0,
          Todayrestusale: r.todayrestPayment ?? 0,
          totalAgentBookings: r.totalAgentBookings ?? 0,
          todayAgentBookings: r.todayAgentBookings ?? 0,
          monthlyAgentBookings: r.monthlyAgentBookings ?? 0,
        });
        const denom =
          roomCount > 0 ? roomCount : todayBooked + todayAvailable + holdQty;
        setExtraData({
          occupancyRate: denom > 0 ? (todayBooked / denom) * 100 : 0,
          avgAddOnPerBooking: todayBooked > 0 ? addRevenue / todayBooked : 0,
        });
      })
      .catch(() => setError('Unable to load dashboard data'))
      .finally(() => {
        if (mounted) setLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, [company?.id, dashQs]);

  // Most-booked rooms — refetches when company or the period filter changes.
  useEffect(() => {
    if (!company?.id) return;
    const token = sessionStorage.getItem('token');
    let mounted = true;
    axios
      .get(
        `${config.API_BASE_URL}/hotelanalytics/toprooms/${company.id}?period=${roomPeriod}`,
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        if (mounted) setTopRooms(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (mounted) setTopRooms([]);
      });
    return () => {
      mounted = false;
    };
  }, [company?.id, roomPeriod]);

  // Recent activity (who did what) — today's log, latest first, top 8.
  useEffect(() => {
    if (!company?.id) return;
    const token = sessionStorage.getItem('token');
    const today = new Date().toISOString().slice(0, 10);
    let mounted = true;
    axios
      .post(
        `${config.API_BASE_URL}/reports/activity-log`,
        {
          companyId: company.id,
          fromDate: today,
          toDate: today,
          page: 1,
          pageSize: 8,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      )
      .then((res) => {
        if (mounted) setActivity(res.data?.items || []);
      })
      .catch(() => {
        if (mounted) setActivity([]);
      });
    return () => {
      mounted = false;
    };
  }, [company?.id]);

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return { text: 'Good Morning', emoji: '🌤️' };
    if (h < 17) return { text: 'Good Afternoon', emoji: '☀️' };
    return { text: 'Good Evening', emoji: '🌙' };
  };

  const greeting = getGreeting();

  const revenueData = [
    { name: 'Today Hotel', amt: data.todaySalePayment, fill: T.blue },
    { name: 'Mthly Hotel', amt: data.monthlysale, fill: T.blueL },
    { name: 'Today Rest', amt: data.Todayrestusale, fill: T.green },
    { name: 'Mthly Rest', amt: data.Monthlyrestusale, fill: '#34d399' },
    { name: 'Add-on', amt: data.addRevenue, fill: T.amber },
  ].filter((i) => i.amt > 0);

  const addonPct =
    data.todaySalePayment > 0
      ? (data.addRevenue / data.todaySalePayment) * 100
      : 0;
  const availablePct =
    (data.todayAvailableRoomQty / (data.roomCount || 1)) * 100 || 0;
  const occPct = Math.min(extraData.occupancyRate || 0, 100);

  const kpiCards = [
    {
      title: 'Net Cash Balance',
      value: data.netCashBalance,
      color: T.blue,
      grad: `linear-gradient(135deg,${T.blue},${T.blueL})`,
      icon: <AccountBalanceWalletIcon />,
      path: '/admin/pettycashmanagement',
      sub: 'All time',
      rbac: P.DASHBOARD_VIEW_NET_CASH,
    },
    {
      title: "Today's Sales",
      value: data.todaySalePayment,
      color: T.green,
      grad: `linear-gradient(135deg,${T.green},#34d399)`,
      icon: <MonetizationOnIcon />,
      path: '/admin/Paymentreport',
      sub: now.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' }),
      rbac: P.DASHBOARD_VIEW_TODAY_REVENUE,
    },
    {
      title: 'Monthly Sales',
      value: data.monthlysale,
      color: T.amber,
      grad: `linear-gradient(135deg,${T.amber},#fcd34d)`,
      icon: <TrendingUpIcon />,
      path: '/admin/Paymentreport',
      sub: now.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' }),
      rbac: P.DASHBOARD_VIEW_MONTHLY_REVENUE,
    },
    {
      title: 'Total Revenue',
      value: data.totalSalePayment,
      color: T.rose,
      grad: `linear-gradient(135deg,${T.rose},#fb7185)`,
      icon: <AttachMoneyIcon />,
      path: '/admin/Paymentreport',
      sub: 'All time',
      rbac: P.DASHBOARD_VIEW_TOTAL_SALES,
    },
    {
      title: 'Add-on Revenue',
      value: data.addRevenue,
      color: T.purple,
      grad: `linear-gradient(135deg,${T.purple},#a78bfa)`,
      icon: <SpeedIcon />,
      sub: 'Today',
      rbac: P.DASHBOARD_VIEW_ADDON_REVENUE,
    },
  ];

  const perfTiles = [
    {
      title: 'Booked Rooms',
      value: data.todayBookedRoomQty,
      icon: <BedIcon />,
      color: T.blue,
      path: '/admin/BookingReport',
      rbac: P.DASHBOARD_VIEW_OCCUPANCY,
    },
    {
      title: 'Available',
      value: data.todayAvailableRoomQty,
      icon: <BedroomParentIcon />,
      color: T.green,
      path: '/admin/manageinventory',
      rbac: P.DASHBOARD_VIEW_AVAILABLE_ROOMS,
    },
    {
      title: 'Check-Ins',
      value: data.totalCheckIn,
      icon: <LoginIcon />,
      color: T.cyan,
      path: '/admin/BookingReport',
      rbac: P.DASHBOARD_VIEW_CHECKINS,
    },
    {
      title: 'Check-Outs',
      value: data.totalCheckOut,
      icon: <LogoutIcon />,
      color: T.rose,
      path: '/admin/BookingReport',
      rbac: P.DASHBOARD_VIEW_CHECKOUTS,
    },
    {
      title: 'Hold Rooms',
      value: data.holdroomqty,
      icon: <LockIcon />,
      color: T.orange,
      path: '/admin/Holdreport',
      rbac: P.DASHBOARD_VIEW_PENDING_INVOICES,
    },
    {
      title: 'Hotels',
      value: data.hotelCount,
      icon: <HotelIcon />,
      color: T.purple,
      path: '/admin/manageinventory',
      rbac: P.DASHBOARD_VIEW_TOTAL_SALES,
    },
    {
      title: 'Today Room Restaurant',
      value: data.Todayrestusale,
      icon: <RestaurantIcon />,
      color: T.pink,
      path: '/admin/Order',
      isAmount: true,
      rbac: P.DASHBOARD_VIEW_RESTAURANT_REVENUE,
    },
    {
      title: 'Monthly Room Restaurant',
      value: data.Monthlyrestusale,
      icon: <RestaurantIcon />,
      color: '#a855f7',
      path: '/admin/Order',
      isAmount: true,
      rbac: P.DASHBOARD_VIEW_RESTAURANT_REVENUE,
    },
    {
      title: 'Total Agent Bookings',
      value: data.totalAgentBookings,
      icon: <SupportAgentIcon />,
      color: T.cyan,
      path: '/admin/BookingReport',
    },
    {
      title: 'Agent Bookings Today',
      value: data.todayAgentBookings,
      icon: <SupportAgentIcon />,
      color: T.blue,
      path: '/admin/BookingReport',
    },
    {
      title: 'Agent Bookings This Month',
      value: data.monthlyAgentBookings,
      icon: <CalendarMonthIcon />,
      color: T.purple,
      path: '/admin/BookingReport',
    },
  ];

  const visibleKpiCards = kpiCards.filter(
    (item) => !item.rbac || hasPermission(item.rbac),
  );
  const visiblePerfTiles = perfTiles.filter(
    (item) => !item.rbac || hasPermission(item.rbac),
  );
  const showAnalytics = hasPermission(P.DASHBOARD_VIEW_ANALYTICS_CHARTS);

  const heroStats = [
    {
      label: "Today's Revenue",
      value: data.todaySalePayment,
      isAmt: true,
      color: '#60a5fa',
      icon: '💰',
      rbac: P.DASHBOARD_VIEW_TODAY_REVENUE,
    },
    {
      label: 'Monthly Revenue',
      value: data.monthlysale,
      isAmt: true,
      color: '#34d399',
      icon: '📈',
      rbac: P.DASHBOARD_VIEW_MONTHLY_REVENUE,
    },
    {
      label: 'Total Revenue',
      value: data.totalSalePayment,
      isAmt: true,
      color: '#fbbf24',
      icon: '🏆',
      rbac: P.DASHBOARD_VIEW_TOTAL_SALES,
    },
    {
      label: 'Occupancy',
      value: extraData.occupancyRate,
      suffix: '%',
      decimals: 1,
      color: '#c084fc',
      icon: '🛏️',
      rbac: P.DASHBOARD_VIEW_OCCUPANCY,
    },
  ].filter((s) => !s.rbac || hasPermission(s.rbac));

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(160deg,#EEF2FF 0%,#F8FAFF 50%,#EFF9FF 100%)`,
        width: '100%',
        maxWidth: '100vw',
        overflowX: 'hidden',
        boxSizing: 'border-box',
        p: { xs: '12px', sm: '16px', md: '22px' },
        pb: { xs: '140px', md: '100px' },
      }}>
      {/* Subscription notice (self-contained; expiry / queued-plan / renew) */}
      <SubscriptionBanner />

      {/* #1: Dashboard date + hotel filter */}
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'flex-end', background: '#fff', border: '1px solid #e5e9f2', borderRadius: 12, padding: 12, marginBottom: 16 }}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>From</label>
          <input type='date' value={dashFrom} onChange={(e) => setDashFrom(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>To</label>
          <input type='date' value={dashTo} onChange={(e) => setDashTo(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1' }} />
        </div>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <label style={{ fontSize: 11, color: '#64748b', fontWeight: 600 }}>Hotel</label>
          <select value={dashHotel} onChange={(e) => setDashHotel(e.target.value)} style={{ padding: 8, borderRadius: 8, border: '1px solid #cbd5e1', minWidth: 160 }}>
            <option value=''>All Hotels</option>
            {dashHotels.map((h) => (<option key={h.id || h.Id} value={h.id || h.Id}>{h.hotelName || h.HotelName}</option>))}
          </select>
        </div>
        <button onClick={applyDashFilter} style={{ padding: '9px 18px', borderRadius: 8, border: 'none', background: '#1d4ed8', color: '#fff', fontWeight: 700, cursor: 'pointer' }}>Apply</button>
        <button onClick={() => { setDashFrom(''); setDashTo(''); setDashHotel(''); setDashQs(''); }} style={{ padding: '9px 14px', borderRadius: 8, border: '1px solid #cbd5e1', background: '#fff', color: '#334155', fontWeight: 600, cursor: 'pointer' }}>Reset</button>
      </div>

      {/* ════════════════════════════════════════
          HERO BANNER
      ════════════════════════════════════════ */}
      <Paper
        elevation={0}
        sx={{
          borderRadius: '20px',
          mb: '20px',
          overflow: 'hidden',
          background:
            'linear-gradient(135deg,#020617 0%,#0f172a 45%,#1e3a8a 100%)',
          position: 'relative',
        }}>
        {/* Decorative blobs */}
        <Box
          sx={{
            position: 'absolute',
            top: -40,
            right: -40,
            width: 180,
            height: 180,
            borderRadius: '50%',
            background:
              'radial-gradient(circle,rgba(96,165,250,0.18),transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: -30,
            left: '30%',
            width: 120,
            height: 120,
            borderRadius: '50%',
            background:
              'radial-gradient(circle,rgba(139,92,246,0.15),transparent 70%)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: '20%',
            left: -20,
            width: 100,
            height: 100,
            borderRadius: '50%',
            background:
              'radial-gradient(circle,rgba(16,185,129,0.12),transparent 70%)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            p: { xs: '18px 16px', md: '22px 26px' },
            position: 'relative',
          }}>
          {/* Top row: greeting + live badge */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              justifyContent: 'space-between',
              mb: '18px',
            }}>
            <Box>
              <Typography
                sx={{
                  fontSize: { xs: '0.65rem', md: '0.72rem' },
                  color: 'rgba(148,163,184,0.8)',
                  fontWeight: 500,
                  mb: '4px',
                  letterSpacing: '0.2px',
                }}>
                {now.toLocaleDateString('en-IN', {
                  weekday: 'long',
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
                &nbsp;·&nbsp;
                {now.toLocaleTimeString([], {
                  hour: '2-digit',
                  minute: '2-digit',
                })}
              </Typography>
              <Typography
                sx={{
                  fontSize: { xs: '1.15rem', md: '1.5rem' },
                  fontWeight: 800,
                  color: '#fff',
                  lineHeight: 1.2,
                  letterSpacing: '-0.4px',
                }}>
                {greeting.emoji}&nbsp;{greeting.text},{' '}
                <span style={{ color: '#60a5fa' }}>{userName}</span>
              </Typography>
            </Box>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end',
                gap: '6px',
              }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  bgcolor: 'rgba(16,185,129,0.15)',
                  border: '1px solid rgba(52,211,153,0.3)',
                  borderRadius: '20px',
                  px: '10px',
                  py: '4px',
                }}>
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#34d399',
                    animation: 'hpulse 2s infinite',
                    '@keyframes hpulse': {
                      '0%,100%': { opacity: 1, transform: 'scale(1)' },
                      '50%': { opacity: 0.5, transform: 'scale(0.85)' },
                    },
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    color: '#34d399',
                    letterSpacing: '0.3px',
                  }}>
                  LIVE
                </Typography>
              </Box>
              {loading && (
                <Typography
                  sx={{
                    fontSize: '0.55rem',
                    color: 'rgba(148,163,184,0.6)',
                    fontStyle: 'italic',
                  }}>
                  Refreshing…
                </Typography>
              )}
            </Box>
          </Box>

          {/* Hero stats */}
          {heroStats.length > 0 && (
            <Grid container spacing={{ xs: 1, md: 1.5 }}>
              {heroStats.map((s, i) => (
                <Grid item xs={6} sm={3} key={i}>
                  <Box
                    sx={{
                      borderRadius: '14px',
                      p: { xs: '10px 12px', md: '12px 16px' },
                      background: 'rgba(255,255,255,0.05)',
                      border: '1px solid rgba(255,255,255,0.1)',
                      backdropFilter: 'blur(12px)',
                      transition: 'background 0.2s',
                      '&:hover': { background: 'rgba(255,255,255,0.09)' },
                    }}>
                    <Typography
                      sx={{
                        fontSize: '0.55rem',
                        color: 'rgba(148,163,184,0.75)',
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '0.6px',
                        mb: '5px',
                      }}>
                      {s.icon}&nbsp;{s.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: { xs: '1rem', md: '1.2rem' },
                        fontWeight: 800,
                        color: s.color,
                        lineHeight: 1,
                        letterSpacing: '-0.3px',
                      }}>
                      {s.isAmt && (
                        <span
                          style={{
                            fontSize: '0.58em',
                            opacity: 0.65,
                            marginRight: 2,
                          }}>
                          {Companycurrency}
                        </span>
                      )}
                      <CountUp
                        end={s.value || 0}
                        separator=','
                        decimals={s.decimals || 0}
                        suffix={s.suffix || ''}
                        duration={1.5}
                      />
                    </Typography>
                  </Box>
                </Grid>
              ))}
            </Grid>
          )}
        </Box>

        {loading && (
          <LinearProgress
            sx={{
              height: 2,
              bgcolor: 'rgba(255,255,255,0.08)',
              '& .MuiLinearProgress-bar': {
                background: 'linear-gradient(90deg,#60a5fa,#34d399)',
              },
            }}
          />
        )}
      </Paper>

      {error && (
        <Paper
          elevation={0}
          sx={{
            mb: '16px',
            p: '10px 16px',
            borderRadius: '10px',
            background: alpha(T.rose, 0.08),
            border: `1px solid ${alpha(T.rose, 0.2)}`,
          }}>
          <Typography
            sx={{ color: T.rose, fontSize: '0.72rem', fontWeight: 500 }}>
            {error}
          </Typography>
        </Paper>
      )}

      {/* ════════════════════════════════════════
          REVENUE KPI CARDS
      ════════════════════════════════════════ */}
      {visibleKpiCards.length > 0 && (
        <>
          <SectionLabel label='Revenue Summary' color={T.blue} />
          <Grid
            container
            spacing={1.5}
            sx={{ mb: '20px', width: '100%', mx: 0 }}>
            {visibleKpiCards.map((item, i) => (
              <Grid item xs={6} sm={4} md={true} key={i} sx={{ minWidth: 0 }}>
                <KpiCard
                  {...item}
                  onClick={item.path ? () => navigate(item.path) : undefined}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ════════════════════════════════════════
          TODAY'S PERFORMANCE TILES
      ════════════════════════════════════════ */}
      {visiblePerfTiles.length > 0 && (
        <>
          <SectionLabel
            label="Today's Performance"
            color={T.green}
            action={
              <Typography
                sx={{ fontSize: '0.58rem', color: T.textMut, fontWeight: 500 }}>
                {now.toLocaleDateString('en-IN', {
                  day: 'numeric',
                  month: 'short',
                  year: 'numeric',
                })}
              </Typography>
            }
          />
          <Grid
            container
            spacing={1.5}
            sx={{ mb: '20px', width: '100%', mx: 0 }}>
            {visiblePerfTiles.map((item, i) => (
              <Grid item xs={6} sm={3} key={i} sx={{ minWidth: 0 }}>
                <MetricTile
                  {...item}
                  onClick={item.path ? () => navigate(item.path) : undefined}
                />
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {/* ════════════════════════════════════════
          ANALYTICS ROW
      ════════════════════════════════════════ */}
      {showAnalytics && (
        <>
          <SectionLabel label='Analytics & Insights' color={T.amber} />
          <Grid container spacing={1.5} sx={{ width: '100%', mx: 0 }}>
            {/* Check-In vs Check-Out */}
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  background: T.card,
                  border: `1px solid ${T.border}`,
                  boxShadow: shadow.card,
                  p: '18px',
                  height: '100%',
                }}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: '14px',
                  }}>
                  <Typography
                    sx={{
                      fontSize: '0.62rem',
                      fontWeight: 700,
                      color: T.textSec,
                      letterSpacing: '0.6px',
                      textTransform: 'uppercase',
                    }}>
                    Check-In vs Out
                  </Typography>
                  <Box sx={{ display: 'flex', gap: '10px' }}>
                    {[
                      { c: T.green, l: 'In' },
                      { c: T.rose, l: 'Out' },
                    ].map((d) => (
                      <Box
                        key={d.l}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}>
                        <Box
                          sx={{
                            width: 8,
                            height: 8,
                            borderRadius: '2px',
                            bgcolor: d.c,
                          }}
                        />
                        <Typography
                          sx={{
                            fontSize: '0.57rem',
                            color: T.textMut,
                            fontWeight: 500,
                          }}>
                          {d.l}
                        </Typography>
                      </Box>
                    ))}
                  </Box>
                </Box>
                <Box sx={{ height: 170 }}>
                  <ResponsiveContainer width='100%' height='100%'>
                    <BarChart
                      data={[
                        { name: 'Check-In', value: data.totalCheckIn },
                        { name: 'Check-Out', value: data.totalCheckOut },
                      ]}
                      margin={{ top: 4, right: 4, left: -24, bottom: 0 }}>
                      <defs>
                        <linearGradient id='bar-in' x1='0' y1='0' x2='0' y2='1'>
                          <stop offset='0%' stopColor={T.green} />
                          <stop offset='100%' stopColor={alpha(T.green, 0.4)} />
                        </linearGradient>
                        <linearGradient
                          id='bar-out'
                          x1='0'
                          y1='0'
                          x2='0'
                          y2='1'>
                          <stop offset='0%' stopColor={T.rose} />
                          <stop offset='100%' stopColor={alpha(T.rose, 0.4)} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray='4 4'
                        stroke='#F1F5F9'
                        vertical={false}
                      />
                      <XAxis
                        dataKey='name'
                        tick={{ fontSize: 10, fill: T.textMut }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <YAxis
                        tick={{ fontSize: 9, fill: T.textMut }}
                        axisLine={false}
                        tickLine={false}
                      />
                      <RechartsTooltip
                        content={<ChartTooltip />}
                        cursor={{ fill: alpha(T.blue, 0.04) }}
                      />
                      <Bar
                        dataKey='value'
                        radius={[8, 8, 0, 0]}
                        maxBarSize={56}>
                        <Cell fill='url(#bar-in)' />
                        <Cell fill='url(#bar-out)' />
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </Box>
              </Paper>
            </Grid>

            {/* Revenue Breakdown */}
            <Grid item xs={12} sm={6} md={4} sx={{ minWidth: 0 }}>
              <Paper
                elevation={0}
                sx={{
                  borderRadius: '16px',
                  background: T.card,
                  border: `1px solid ${T.border}`,
                  boxShadow: shadow.card,
                  p: '18px',
                  height: '100%',
                }}>
                <Typography
                  sx={{
                    fontSize: '0.62rem',
                    fontWeight: 700,
                    color: T.textSec,
                    letterSpacing: '0.6px',
                    textTransform: 'uppercase',
                    mb: '14px',
                  }}>
                  Revenue Breakdown
                </Typography>
                {revenueData.length > 0 ? (
                  <Box sx={{ height: 185 }}>
                    <ResponsiveContainer width='100%' height='100%'>
                      <BarChart
                        data={revenueData}
                        margin={{ top: 4, right: 4, left: -20, bottom: 34 }}>
                        <CartesianGrid
                          strokeDasharray='4 4'
                          stroke='#F1F5F9'
                          vertical={false}
                        />
                        <XAxis
                          dataKey='name'
                          angle={-30}
                          textAnchor='end'
                          tick={{ fontSize: 8, fill: T.textMut }}
                          axisLine={false}
                          tickLine={false}
                          interval={0}
                        />
                        <YAxis
                          tickFormatter={(v) => `${(v / 1000).toFixed(0)}k`}
                          tick={{ fontSize: 8, fill: T.textMut }}
                          axisLine={false}
                          tickLine={false}
                        />
                        <RechartsTooltip
                          content={<ChartTooltip />}
                          cursor={{ fill: alpha(T.blue, 0.04) }}
                        />
                        <Bar
                          dataKey='amt'
                          radius={[6, 6, 0, 0]}
                          maxBarSize={40}>
                          {revenueData.map((e, i) => (
                            <Cell key={i} fill={e.fill} />
                          ))}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      height: 185,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: '8px',
                    }}>
                    <Typography sx={{ fontSize: '2rem' }}>📊</Typography>
                    <Typography
                      sx={{
                        color: T.textMut,
                        fontSize: '0.72rem',
                        fontWeight: 500,
                      }}>
                      No revenue data today
                    </Typography>
                  </Box>
                )}
              </Paper>
            </Grid>

            {/* Right Stats Column */}
            <Grid item xs={12} sm={12} md={4} sx={{ minWidth: 0 }}>
              <Grid container spacing={1.5} sx={{ height: '100%' }}>
                {/* Occupancy Ring Card */}
                <Grid item xs={12} sm={4} md={12}>
                  <Paper
                    elevation={0}
                    sx={{
                      borderRadius: '16px',
                      background:
                        'linear-gradient(135deg,#0f172a 0%,#1e3a8a 60%,#1A56DB 100%)',
                      boxShadow: `0 8px 32px ${alpha(T.blue, 0.3)}`,
                      p: '18px',
                      position: 'relative',
                      overflow: 'hidden',
                    }}>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -24,
                        right: -24,
                        width: 100,
                        height: 100,
                        borderRadius: '50%',
                        bgcolor: 'rgba(96,165,250,0.12)',
                        pointerEvents: 'none',
                      }}
                    />
                    <Box
                      sx={{
                        position: 'absolute',
                        bottom: -10,
                        left: '40%',
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        bgcolor: 'rgba(139,92,246,0.15)',
                        pointerEvents: 'none',
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: '0.6rem',
                        fontWeight: 700,
                        color: 'rgba(148,163,184,0.8)',
                        letterSpacing: '0.6px',
                        textTransform: 'uppercase',
                        mb: '12px',
                      }}>
                      🏨 Occupancy Rate
                    </Typography>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '14px',
                      }}>
                      <OccupancyRing pct={occPct} size={84} />
                      <Box>
                        <Typography
                          sx={{
                            fontSize: '2.2rem',
                            fontWeight: 900,
                            color: '#fff',
                            lineHeight: 1,
                            letterSpacing: '-1px',
                          }}>
                          <CountUp
                            end={occPct}
                            decimals={1}
                            suffix='%'
                            duration={1.6}
                          />
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.6rem',
                            color: 'rgba(148,163,184,0.7)',
                            mt: '3px',
                          }}>
                          {data.todayBookedRoomQty} of {data.roomCount || 0}{' '}
                          rooms
                        </Typography>
                        <Typography
                          sx={{
                            fontSize: '0.58rem',
                            color: 'rgba(148,163,184,0.55)',
                            mt: '2px',
                          }}>
                          Avg add-on: {Companycurrency}{' '}
                          <CountUp
                            end={extraData.avgAddOnPerBooking || 0}
                            decimals={0}
                            separator=','
                            duration={1.4}
                          />
                        </Typography>
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Available Rooms */}
                <Grid item xs={6} sm={4} md={12}>
                  <StatMini
                    label='Available Rooms'
                    value={data.todayAvailableRoomQty}
                    color={T.green}
                    icon={<BedroomParentIcon />}
                    pct={availablePct}
                    pctLabel={`${availablePct.toFixed(0)}% of ${data.roomCount || 0} total rooms`}
                  />
                </Grid>

                {/* Add-on Revenue */}
                <Grid item xs={6} sm={4} md={12}>
                  <StatMini
                    label='Add-on Revenue'
                    value={data.addRevenue}
                    color={T.amber}
                    icon={<AttachMoneyIcon />}
                    pct={addonPct}
                    pctLabel={`${addonPct.toFixed(1)}% of today's sales`}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </>
      )}

      {/* ── MOST BOOKED ROOMS ── */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: '16px',
          background: T.card,
          border: `1px solid ${alpha(T.blue, 0.12)}`,
          boxShadow: shadow.card,
          p: { xs: 1.8, md: 2.4 },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 1,
            mb: 1.6,
          }}>
          <Typography
            sx={{
              fontSize: '0.95rem',
              fontWeight: 800,
              color: T.textPri,
              display: 'flex',
              alignItems: 'center',
              gap: 0.6,
            }}>
            🏆 Most Booked Rooms
          </Typography>
          {/* Date filter: Today / Weekly / Monthly */}
          <Box
            sx={{
              display: 'flex',
              gap: 0.5,
              bgcolor: alpha(T.blue, 0.07),
              p: 0.4,
              borderRadius: 2,
            }}>
            {[
              ['today', 'Today'],
              ['week', 'Weekly'],
              ['month', 'Monthly'],
            ].map(([val, label]) => (
              <Box
                key={val}
                onClick={() => setRoomPeriod(val)}
                sx={{
                  px: 1.4,
                  py: 0.5,
                  borderRadius: 1.5,
                  cursor: 'pointer',
                  fontSize: '0.72rem',
                  fontWeight: 700,
                  userSelect: 'none',
                  transition: 'all 0.15s',
                  bgcolor: roomPeriod === val ? T.blue : 'transparent',
                  color: roomPeriod === val ? '#fff' : T.textSec,
                }}>
                {label}
              </Box>
            ))}
          </Box>
        </Box>

        {topRooms.length === 0 ? (
          <Typography
            sx={{
              fontSize: '0.8rem',
              color: T.textSec,
              py: 2,
              textAlign: 'center',
            }}>
            No room bookings in this period yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>
            {topRooms.map((r) => {
              const rankColor =
                r.rank === 1
                  ? '#f59e0b'
                  : r.rank === 2
                    ? '#94a3b8'
                    : r.rank === 3
                      ? '#b45309'
                      : alpha(T.blue, 0.55);
              return (
                <Box
                  key={r.roomId}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.2,
                    p: 1,
                    borderRadius: 2,
                    bgcolor: alpha(T.blue, 0.03),
                  }}>
                  <Box
                    sx={{
                      width: 26,
                      height: 26,
                      flexShrink: 0,
                      borderRadius: '50%',
                      bgcolor: rankColor,
                      color: '#fff',
                      fontSize: '0.72rem',
                      fontWeight: 800,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}>
                    {r.rank}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: T.textPri,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}>
                      {r.roomName || `Room ${r.roomNumber}`}
                    </Typography>
                    {r.roomType && (
                      <Typography
                        sx={{ fontSize: '0.68rem', color: T.textSec }}>
                        {r.roomType}
                      </Typography>
                    )}
                  </Box>
                  <Typography
                    sx={{
                      fontSize: '0.72rem',
                      fontWeight: 700,
                      color: T.textSec,
                      flexShrink: 0,
                    }}>
                    {r.nights} night{r.nights > 1 ? 's' : ''}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: '0.82rem',
                      fontWeight: 800,
                      color: T.green,
                      minWidth: 70,
                      textAlign: 'right',
                      flexShrink: 0,
                    }}>
                    {Companycurrency}
                    {Number(r.revenue || 0).toLocaleString()}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        )}
      </Paper>

      {/* ── RECENT ACTIVITY (who did what) ── */}
      <Paper
        elevation={0}
        sx={{
          mt: 2,
          borderRadius: '16px',
          background: T.card,
          border: `1px solid ${alpha(T.blue, 0.12)}`,
          boxShadow: shadow.card,
          p: { xs: 1.8, md: 2.4 },
        }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.4,
          }}>
          <Typography
            sx={{ fontSize: '0.95rem', fontWeight: 800, color: T.textPri }}>
            🕓 Recent Activity
          </Typography>
          <Typography
            onClick={() => navigate('/admin/ActivityLog')}
            sx={{
              fontSize: '0.72rem',
              fontWeight: 700,
              color: T.blue,
              cursor: 'pointer',
            }}>
            View all →
          </Typography>
        </Box>
        {activity.length === 0 ? (
          <Typography
            sx={{ fontSize: '0.8rem', color: T.textSec, py: 1.5, textAlign: 'center' }}>
            No activity recorded today yet.
          </Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.6 }}>
            {activity.map((a, i) => (
              <Box
                key={i}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  p: 0.9,
                  borderRadius: 2,
                  bgcolor: alpha(T.blue, 0.03),
                }}>
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    flexShrink: 0,
                    bgcolor:
                      a.severity === 'High' || a.severity === 'Critical'
                        ? T.rose
                        : T.green,
                  }}
                />
                <Typography
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    fontSize: '0.8rem',
                    color: T.textPri,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}>
                  <b>{a.userName || a.performedBy || 'User'}</b>{' '}
                  {(() => {
                    // #4: human-readable action derived from the endpoint (works on
                    // existing rows too) — "placed a restaurant order" etc. instead
                    // of "post Restaurant".
                    const u = `${a.clientUrl || ''} ${a.entityName || ''}`.toLowerCase();
                    const h = (s) => u.includes(s);
                    if (h('saveorder')) return 'placed a restaurant order';
                    if (h('cancelitem') || h('cancelroomitem'))
                      return 'cancelled an order item';
                    if (h('updateitem') || h('updateroomitem'))
                      return 'edited an order item';
                    if (h('updaterestaurantpayment'))
                      return 'edited a restaurant payment';
                    if (h('savepayment') || h('addpayment'))
                      return 'added a payment';
                    if (h('generateinvoice') || h('saveinvoice'))
                      return 'generated an invoice';
                    if (h('undoinvoice')) return 'reverted an invoice';
                    if (h('checkinbooking')) return 'checked in a guest';
                    if (h('undocheckin')) return 'undid a check-in';
                    if (h('removeearlycheckincharge'))
                      return 'removed an early check-in charge';
                    if (h('bookpackage') || h('savebooking') || h('savehotelbokking'))
                      return 'created a booking';
                    if (h('cancelbooking')) return 'cancelled a booking';
                    if (h('updatebookingdetails') || h('updateuserdetails'))
                      return 'updated a booking';
                    if (h('adddiscount')) return 'applied a discount';
                    if (h('cover')) return 'added a cover charge';
                    if (h('createitem')) return 'created a menu item';
                    if (h('updaterestitem') || h('updateitemimage'))
                      return 'updated a menu item';
                    if (h('createcategory')) return 'created a category';
                    if (h('adddepartment')) return 'added a department';
                    if (h('createuser')) return 'created a user';
                    if (h('override') || h('rbac') || h('permission'))
                      return 'updated permissions';
                    if (h('savehotel') || h('updatehotel'))
                      return 'saved hotel setup';
                    if (h('company')) return 'updated company details';
                    if (h('send')) return 'sent a notification';
                    const m = (a.actionType || '').toUpperCase();
                    const v =
                      m === 'DELETE'
                        ? 'deleted'
                        : m === 'PUT' || m === 'PATCH'
                          ? 'updated'
                          : 'saved';
                    return `${v} ${a.moduleName || a.entityName || 'a record'}`;
                  })()}
                </Typography>
                <Typography
                  sx={{ fontSize: '0.7rem', color: T.textMut, flexShrink: 0 }}>
                  {a.performedAt
                    ? new Date(a.performedAt).toLocaleTimeString('en-IN', {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : ''}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Paper>

      {/* ── BOOKING CALENDAR — loads only on click (heavy fetch). ── */}
      <Box sx={{ mt: 2 }}>
        {showCalendar ? (
          <Calender />
        ) : (
          <Paper
            elevation={0}
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 3,
              border: '1px dashed',
              borderColor: 'divider',
            }}>
            <CalendarMonthIcon
              sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }}
            />
            <Typography sx={{ mb: 2, color: 'text.secondary' }}>
              Booking Calendar
            </Typography>
            <Button
              variant='contained'
              startIcon={<CalendarMonthIcon />}
              onClick={() => setShowCalendar(true)}>
              Load Calendar
            </Button>
          </Paper>
        )}
      </Box>

      {/* ── FLOATING BUTTONS ── */}
      <Box
        sx={{
          position: 'fixed',
          bottom: { xs: 14, md: 26 },
          right: { xs: 14, md: 26 },
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: 'flex-end',
          gap: '10px',
          zIndex: 1300,
        }}>
        {hasPermission(P.PAGE_BOOKINGS) && (
          <Tooltip title='View Calendar' placement='left' arrow>
            <Fab
              size='small'
              onClick={() => navigate('/admin/Calender')}
              sx={{
                width: 42,
                height: 42,
                background: T.card,
                color: T.blue,
                border: `1.5px solid ${alpha(T.blue, 0.2)}`,
                boxShadow: `0 4px 16px ${alpha(T.blue, 0.2)}`,
                '&:hover': {
                  transform: 'scale(1.1)',
                  background: '#EEF2FF',
                  boxShadow: `0 6px 24px ${alpha(T.blue, 0.3)}`,
                },
                transition: 'all 0.2s',
              }}>
              <CalendarMonthIcon sx={{ fontSize: 19 }} />
            </Fab>
          </Tooltip>
        )}

        {hasPermission(P.BOOKING_CREATE) && (
          <Fab
            variant='extended'
            onClick={() => navigate('/admin/manageinventory')}
            sx={{
              height: 42,
              px: '18px',
              borderRadius: '12px',
              background: `linear-gradient(135deg,${T.blue},${T.blueL})`,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'none',
              boxShadow: `0 4px 18px ${alpha(T.blue, 0.4)}`,
              gap: '6px',
              whiteSpace: 'nowrap',
              letterSpacing: '0.1px',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 8px 28px ${alpha(T.blue, 0.5)}`,
              },
              transition: 'all 0.2s',
            }}>
            <AddIcon sx={{ fontSize: 17 }} /> New Booking
          </Fab>
        )}

        {hasPermission(P.PAGE_CREATE_ORDER) && (
          <Fab
            variant='extended'
            onClick={() => navigate('/admin/CreateOrder')}
            sx={{
              height: 42,
              px: '18px',
              borderRadius: '12px',
              background: `linear-gradient(135deg,${T.green},#34d399)`,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.75rem',
              textTransform: 'none',
              boxShadow: `0 4px 18px ${alpha(T.green, 0.4)}`,
              gap: '6px',
              whiteSpace: 'nowrap',
              letterSpacing: '0.1px',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: `0 8px 28px ${alpha(T.green, 0.5)}`,
              },
              transition: 'all 0.2s',
            }}>
            <AddIcon sx={{ fontSize: 17 }} /> Create Order
          </Fab>
        )}
      </Box>
    </Box>
  );
}